/** Tokens visuais — Painel do Paciente Klinvia (frontend-design + agendaTheme) */
import { AGENDA_THEME } from '../pages/agendaTheme';
import { ALL_APPOINTMENT_TYPE_LABELS } from '../constants/appointmentTypes';

export const PATIENT_PANEL_THEME = {
  ...AGENDA_THEME,
  headerBg: AGENDA_THEME.muted,
  contentBg: AGENDA_THEME.paper,
  profileBg: AGENDA_THEME.muted,
  sectionTitle: '#115E59',
  alertBorder: '#FBBF24',
  alertBg: '#FFFBEB',
} as const;

export const patientPanelClinicalGridBg = {
  backgroundColor: PATIENT_PANEL_THEME.headerBg,
  backgroundImage: `
    linear-gradient(${PATIENT_PANEL_THEME.gridLine} 1px, transparent 1px),
    linear-gradient(90deg, ${PATIENT_PANEL_THEME.gridLine} 1px, transparent 1px)
  `,
  backgroundSize: '24px 24px',
};

export const patientPanelShellSx = {
  ...patientPanelClinicalGridBg,
  borderLeft: { xs: 'none', md: `1px solid ${PATIENT_PANEL_THEME.borderSoft}` },
};

/** Espaçamento horizontal do cabeçalho; barras de largura total usam o valor negativo. */
export const PATIENT_PANEL_HEADER_PX = { xs: 1.25, sm: 1.5 } as const;
export const PATIENT_PANEL_HEADER_BLEED = { xs: -1.25, sm: -1.5 } as const;

export const patientPanelHeaderSx = {
  ...patientPanelClinicalGridBg,
  borderBottom: `1px solid ${PATIENT_PANEL_THEME.borderSoft}`,
  px: PATIENT_PANEL_HEADER_PX,
  pt: 1,
  pb: 0,
};

export const patientPanelActionButtonSx = {
  borderColor: PATIENT_PANEL_THEME.borderSoft,
  color: PATIENT_PANEL_THEME.tealDark,
  bgcolor: PATIENT_PANEL_THEME.paper,
  textTransform: 'none' as const,
  borderRadius: 2,
  fontSize: '0.75rem',
  fontWeight: 600,
  height: 30,
  minWidth: 0,
  px: 1.25,
  whiteSpace: 'nowrap' as const,
  boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
  '& .MuiButton-startIcon': { mr: 0.5, '& > *:nth-of-type(1)': { fontSize: 16 } },
  '&:hover': {
    borderColor: PATIENT_PANEL_THEME.teal,
    bgcolor: PATIENT_PANEL_THEME.tealSoft,
  },
};

export const patientPanelPrimaryActionButtonSx = {
  ...patientPanelActionButtonSx,
  color: '#fff',
  bgcolor: PATIENT_PANEL_THEME.teal,
  borderColor: PATIENT_PANEL_THEME.teal,
  '&:hover': {
    bgcolor: PATIENT_PANEL_THEME.tealDark,
    borderColor: PATIENT_PANEL_THEME.tealDark,
  },
};

export const patientPanelQuickActionButtonSx = {
  ...patientPanelActionButtonSx,
  borderColor: PATIENT_PANEL_THEME.border,
  bgcolor: PATIENT_PANEL_THEME.tealSoft,
  '&:hover': {
    borderColor: PATIENT_PANEL_THEME.teal,
    bgcolor: PATIENT_PANEL_THEME.tealMuted,
  },
};

export const patientPanelIconButtonSx = {
  width: 30,
  height: 30,
  borderRadius: 2,
  color: PATIENT_PANEL_THEME.tealDark,
  bgcolor: PATIENT_PANEL_THEME.paper,
  border: `1px solid ${PATIENT_PANEL_THEME.borderSoft}`,
  '& svg': { fontSize: 17 },
  '&:hover': {
    bgcolor: PATIENT_PANEL_THEME.tealSoft,
    borderColor: PATIENT_PANEL_THEME.teal,
  },
};

export const patientPanelCloseButtonSx = {
  width: 30,
  height: 30,
  borderRadius: 2,
  color: PATIENT_PANEL_THEME.slate,
  bgcolor: PATIENT_PANEL_THEME.paper,
  border: `1px solid ${PATIENT_PANEL_THEME.borderSoft}`,
  '& svg': { fontSize: 17 },
  '&:hover': { bgcolor: PATIENT_PANEL_THEME.mutedAlt },
};

export const patientPanelBackBarSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.25,
  mx: PATIENT_PANEL_HEADER_BLEED,
  mt: -1,
  mb: 0.75,
  px: { xs: 0.5, sm: 0.75 },
  py: 0.15,
  borderBottom: `1px solid ${PATIENT_PANEL_THEME.borderSoft}`,
  bgcolor: PATIENT_PANEL_THEME.paper,
} as const;

/** Faixa das abas: ocupa a largura total do cabeçalho. */
export const patientPanelTabsBarSx = {
  mx: PATIENT_PANEL_HEADER_BLEED,
  mt: 0.75,
} as const;

export const patientPanelBackButtonSx = {
  color: PATIENT_PANEL_THEME.tealDark,
  '&:hover': { bgcolor: PATIENT_PANEL_THEME.tealSoft },
} as const;

export const patientPanelBackLabelSx = {
  border: 0,
  background: 'none',
  cursor: 'pointer',
  fontWeight: 700,
  fontSize: '0.875rem',
  lineHeight: 1.25,
  color: PATIENT_PANEL_THEME.tealDark,
  p: 0.5,
  m: 0,
  fontFamily: 'inherit',
} as const;

export const patientPanelTabsSx = {
  minHeight: 36,
  '& .MuiTabs-flexContainer': {
    gap: 0.25,
  },
  '& .MuiTabs-scrollButtons': {
    width: 28,
    color: PATIENT_PANEL_THEME.slate,
    '&.Mui-disabled': { opacity: 0, width: 0 },
  },
  '& .MuiTab-root': {
    color: PATIENT_PANEL_THEME.slate,
    fontWeight: 600,
    textTransform: 'none' as const,
    minHeight: 36,
    minWidth: 0,
    py: 0.5,
    fontSize: '0.78rem',
    px: 1.25,
    maxWidth: 'none',
    '& .MuiTab-iconWrapper': { mr: 0.5 },
  },
  '& .Mui-selected': { color: `${PATIENT_PANEL_THEME.tealDark} !important` },
  '& .MuiTabs-indicator': {
    bgcolor: PATIENT_PANEL_THEME.teal,
    height: 3,
    borderRadius: '3px 3px 0 0',
  },
};

export function patientPanelMobileTabSx(selected: boolean) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.45,
    flex: '0 0 auto',
    px: 1,
    py: 0.5,
    cursor: 'pointer',
    userSelect: 'none' as const,
    whiteSpace: 'nowrap' as const,
    color: selected ? PATIENT_PANEL_THEME.tealDark : PATIENT_PANEL_THEME.slate,
    fontWeight: 600,
    fontSize: '0.72rem',
    borderRadius: '8px 8px 0 0',
    borderBottom: selected ? `3px solid ${PATIENT_PANEL_THEME.teal}` : '3px solid transparent',
    bgcolor: selected ? PATIENT_PANEL_THEME.tealSoft : 'transparent',
  };
}

export const patientPanelBadgeSx = {
  '& .MuiBadge-badge': {
    bgcolor: PATIENT_PANEL_THEME.paper,
    color: PATIENT_PANEL_THEME.teal,
    fontSize: '0.6rem',
    minWidth: 16,
    height: 16,
    border: `1px solid ${PATIENT_PANEL_THEME.border}`,
  },
};

export const patientPanelContextBannerSx = {
  mb: 1.5,
  px: 1.25,
  py: 1,
  borderRadius: 2,
  border: `1px solid ${PATIENT_PANEL_THEME.border}`,
  bgcolor: PATIENT_PANEL_THEME.tealSoft,
  borderLeft: `4px solid ${PATIENT_PANEL_THEME.teal}`,
  display: 'flex',
  flexWrap: 'wrap' as const,
  alignItems: 'center',
  gap: 1,
};

/** Coluna lateral à esquerda do painel do paciente na agenda (consulta + procedimentos). */
export const patientPanelContextRailSx = {
  width: { xs: 0, sm: 140, md: 160, lg: 172 },
  display: { xs: 'none', sm: 'flex' },
  flexShrink: 0,
  alignSelf: 'stretch',
  flexDirection: 'column' as const,
  gap: 1,
  p: { xs: 1.25, md: 1.5 },
  borderRight: `1px solid ${PATIENT_PANEL_THEME.borderSoft}`,
  bgcolor: PATIENT_PANEL_THEME.tealSoft,
  borderLeft: `4px solid ${PATIENT_PANEL_THEME.teal}`,
  overflowY: 'auto' as const,
  minHeight: 0,
};

export const patientPanelContextDotSx = (color: string) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  bgcolor: color,
  flexShrink: 0,
});

export const patientPanelContentSx = {
  p: 0,
  bgcolor: PATIENT_PANEL_THEME.contentBg,
  overflowY: 'auto' as const,
  overflowX: 'hidden' as const,
  flex: 1,
  minHeight: 0,
  WebkitOverflowScrolling: 'touch' as const,
  /** pan-x permite scroll horizontal em tabelas (ex.: contas a receber) no iOS. */
  touchAction: 'pan-x pan-y' as const,
};

export const patientPanelLoadingSkeletonSx = {
  p: { xs: 2, sm: 2.5 },
  bgcolor: PATIENT_PANEL_THEME.profileBg,
};

export const APPOINTMENT_TYPE_LABELS: Record<string, string> = ALL_APPOINTMENT_TYPE_LABELS;

/** Status alinhados à agenda (sem roxo). */
export const patientPanelStatusConfig: Record<string, { label: string; bg: string; color: string; textColor?: string }> = {
  scheduled: { label: 'Agendado', bg: PATIENT_PANEL_THEME.mutedAlt, color: PATIENT_PANEL_THEME.slate },
  confirmed: { label: 'Confirmado', bg: '#16a34a', color: '#16a34a', textColor: '#ffffff' },
  waiting: { label: 'Na espera', bg: '#fffbeb', color: '#b45309' },
  in_progress: { label: 'Em Atend.', bg: PATIENT_PANEL_THEME.tealSoft, color: PATIENT_PANEL_THEME.tealDark },
  attended: { label: 'Atendido', bg: '#ecfdf5', color: PATIENT_PANEL_THEME.primary },
  completed: { label: 'Concluído', bg: '#f0fdf4', color: '#166534' },
  rescheduled: { label: 'Reagendar', bg: '#fff7ed', color: '#c2410c' },
  cancelled: { label: 'Cancelado', bg: PATIENT_PANEL_THEME.cancelledBg, color: PATIENT_PANEL_THEME.cancelledText },
  no_show: { label: 'Não Compareceu', bg: PATIENT_PANEL_THEME.mutedAlt, color: PATIENT_PANEL_THEME.slate },
  draft: { label: 'Rascunho', bg: '#f3f4f6', color: '#6b7280' },
  sent: { label: 'Enviado', bg: '#dbeafe', color: '#1d4ed8' },
  pending_approval: { label: 'Aguardando aprovação', bg: '#fef3c7', color: '#b45309' },
  approved: { label: 'Aprovado', bg: '#d1fae5', color: '#065f46' },
  rejected: { label: 'Recusado', bg: '#fee2e2', color: '#dc2626' },
  active: { label: 'Ativo', bg: '#d1fae5', color: '#059669' },
  pending: { label: 'Pendente', bg: '#fef3c7', color: '#d97706' },
  paid: { label: 'Pago', bg: '#d1fae5', color: '#059669' },
  overdue: { label: 'Vencido', bg: '#fee2e2', color: '#dc2626' },
};

export function getPatientPanelStatusConfig(status: string) {
  return patientPanelStatusConfig[status] || { label: status, bg: '#f3f4f6', color: '#6b7280' };
}
